import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Sentry from '@sentry/react';
import { App } from './App';
import { queryClient } from './config/queryClient';
import { initializeSentry } from './config/sentry';
import { SentryErrorBoundary } from './components/SentryErrorBoundary';
import { setupSentryFetchInterceptor } from './utils/sentryApiInterceptor';
import './styles/index.css';

// Initialize Sentry BEFORE anything else
initializeSentry();

// Setup fetch interceptor for automatic API monitoring
setupSentryFetchInterceptor();

// Aggressively clear CacheStorage on each load to avoid stale assets during development/deploys
if ('caches' in window) {
  caches
    .keys()
    .then(cacheNames =>
      Promise.all(cacheNames.map(name => caches.delete(name)))
    )
    .catch(() => {
      // ignore cache clear errors
    });
}

// Register service worker for caching
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then(registration => {
        console.log(
          'Service Worker registered successfully:',
          registration.scope
        );
      })
      .catch(error => {
        console.log('Service Worker registration failed:', error);
      });
  });
}

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');

// Create Sentry-wrapped React Router for automatic route tracking
const SentryRoutes = Sentry.withSentryRouting(BrowserRouter);

const root = createRoot(container);
root.render(
  <React.StrictMode>
    <SentryErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SentryRoutes>
          <App />
        </SentryRoutes>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </SentryErrorBoundary>
  </React.StrictMode>
);
